/**
 * PDP blocks - PE (Promotion alert, Countdown timer)
 * Behavior port of Hyper's product-promotion-alert (product-info.js) and
 * countdown-timer.js — no libraries, deferred, paex namespace.
 */

if (!customElements.get('paex-promotion-alert')) {
  customElements.define(
    'paex-promotion-alert',
    class PaexPromotionAlert extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const button = this.querySelector('button');
        if (button) {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              const blockEl = this.closest('.paex-pdp-block');
              if (blockEl) blockEl.hidden = true;
            },
            { signal: this.abortController.signal }
          );
        }
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}

if (!customElements.get('paex-countdown-timer')) {
  customElements.define(
    'paex-countdown-timer',
    class PaexCountdownTimer extends HTMLElement {
      connectedCallback() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;

        this.initCountdown();
      }

      disconnectedCallback() {
        if (this.intervalId) clearInterval(this.intervalId);
      }

      initCountdown() {
        if (this.type === 'fixed_time') {
          const dueDate = this.dataset.dueDate;
          if (dueDate) this.targetTime = new Date(dueDate).getTime();
        } else {
          /* Evergreen: due date derived from the cycle; hides on expiry until next page load (Hyper parity) */
          this.targetTime = this.getEvergreenDueDate().getTime();
        }

        if (!this.targetTime || Number.isNaN(this.targetTime)) return;

        if (this.targetTime > Date.now()) {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      getEvergreenDueDate() {
        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1);
            dueDate.setDate(1);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week': {
            const daysUntilNextWeek = 7 - now.getDay();
            dueDate.setDate(now.getDate() + daysUntilNextWeek);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          }
          case 'every_2_hrs':
            return this.getNextIntervalDate(now, dueDate, 2);
          case 'every_6_hrs':
            return this.getNextIntervalDate(now, dueDate, 6);
          case 'every_12_hrs':
            return this.getNextIntervalDate(now, dueDate, 12);
          case 'every_24_hrs':
          default:
            dueDate.setDate(dueDate.getDate() + 1);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
        }
      }

      getNextIntervalDate(now, dueDate, intervalHours) {
        for (let hour = intervalHours; hour <= 24; hour += intervalHours) {
          if (hour > now.getHours()) {
            dueDate.setHours(hour, 0, 0, 0);
            return dueDate;
          }
        }
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate;
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.updateDisplay(this.targetTime - Date.now());
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);
        this.classList.remove('paex-countdown--hidden');
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        this.classList.add('paex-countdown--hidden');
      }

      updateDisplay(remainingTime) {
        const components = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(components[unit]);
          }
        });
      }

      computeTimeComponents(remainingTime) {
        return {
          day: Math.floor(remainingTime / this.MS_IN_DAY),
          hour: Math.floor((remainingTime % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((remainingTime % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((remainingTime % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return value < 10 ? `0${value}` : `${value}`;
      }
    }
  );
}
